import { memo } from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { getLeadingAccountConnection } from "@common/helpers/financialAccount";
import { getIntegrationAccountOfConnection } from "@helpers/financialAccount";
import { EntityManagementDropdown } from "@components/entity/EntityManagementDropdown";
import { FinancialAccountManagementDropdown } from "@components/financialAccount/FinancialAccountManagementDropdown";
import { BrokenConnectionLink } from "@components/financialConnection/BrokenConnectionLink";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { hasFailedConnection } from "../accountsView.helpers";
import { AccountsTableRow } from "../accountsView.types";
import styles from "../accountsView.module.scss";

export const ActionsCell = memo(
    ({ row }: CellContext<ViewTableFeatures, AccountsTableRow, unknown>) => {
        const rowData = row.original;
        if (!rowData.account) {
            return (
                <ViewTableCell className={styles.actionsCell}>
                    <div
                        className={classNames(
                            styles.actions,
                            viewTableStyles.showOnHover,
                        )}
                    >
                        <EntityManagementDropdown entity={rowData.entity} />
                    </div>
                </ViewTableCell>
            );
        }

        const connection = getLeadingAccountConnection(rowData.account);
        const hasConnectionError = hasFailedConnection(rowData.account);
        const integrationAccountOfLeadingConnection =
            connection &&
            getIntegrationAccountOfConnection(rowData.account, connection);

        return (
            <ViewTableCell className={styles.actionsCell}>
                <div
                    className={classNames(styles.actions, {
                        [viewTableStyles.showOnHover]: !hasConnectionError,
                    })}
                >
                    {hasConnectionError &&
                        connection &&
                        integrationAccountOfLeadingConnection && (
                            <BrokenConnectionLink
                                connection={connection}
                                integrationAccount={
                                    integrationAccountOfLeadingConnection
                                }
                            />
                        )}
                    <FinancialAccountManagementDropdown
                        account={rowData.account}
                        connection={connection ?? undefined}
                    />
                </div>
            </ViewTableCell>
        );
    },
);

ActionsCell.displayName = "ActionsCell";
